import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { getUserProfile } from "./function";
import Spinner from "@/components/Spinner";
import { Helmet } from "react-helmet-async";
import { User, Lock } from "lucide-react";

interface TabConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const AdminProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const userData = await getUserProfile();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        // handle or log error
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const tabs: TabConfig[] = [
    {
      id: "edit",
      label: t("adminProfile.tabs.editProfile", "Edit Profile"),
      path: "/admin/profile",
      icon: <User className="w-4 h-4" />,
    },
    {
      id: "password",
      label: t("adminProfile.tabs.changePassword", "Change Password"),
      path: "/admin/profile/change-password",
      icon: <Lock className="w-4 h-4" />,
    },
  ];

  const getCurrentTab = React.useCallback(() => {
    if (location.pathname === "/admin/profile/change-password") return "password";
    return "edit";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getCurrentTab());

  useEffect(() => {
    setActiveTab(getCurrentTab());
  }, [getCurrentTab]);

  const handleTabChange = (tab: TabConfig) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case "password":
        return t("adminProfile.changePassword", "Change Password");
      default:
        return t("adminProfile.editProfile", "Edit Profile");
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t("nav.dashboard", "Dashboard"), path: "/admin/dashboard" },
    { label: t("adminProfile.title", "My Profile") },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>{getPageTitle()} | BEQ Reward Admin</title>
      </Helmet>

      <PageHeader title={t("adminProfile.title", "My Profile")} breadcrumbs={breadcrumbs} />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? "!text-blue-600 dark:!text-blue-400"
                    : "!text-gray-500 dark:!text-gray-400 hover:!text-gray-700 dark:hover:!text-gray-300"
                }`}
              >
                {tab.icon}
                {tab.label}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {user ? <Outlet context={{ user }} /> : <Spinner />}
        </div>
      </div>
    </div>
  );
};

export default AdminProfilePage;
